import { CLIENT_FUNDS_TYPES, TCaseLedgerType } from "../../codecs/CaseLedger";
import { TRoadblockCaseData, TRoadblockConfiguration } from "../roadblockTypesAndCodecs";
import { fact, formatEvidenceDate, neutralFact } from "../evidence/evidenceHelpers";
import { TRoadblockEvidenceLink } from "../../codecs/form/RoadblockForm";
import { anyReceivedDocumentExists } from "./SharedExchangeAndPreCompletionItems";

const clientFundsLink: TRoadblockEvidenceLink = { block: "case_details", section: "client_funds" };
const outgoingPaymentsLink: TRoadblockEvidenceLink = { block: "case_details", section: "outgoing_payments" };
const chargesLink: TRoadblockEvidenceLink = { block: "charges_and_restrictions", section: null };

const LENDER_FUNDS_TYPES: ReadonlyArray<TCaseLedgerType> = ["mortgage_advance"];

const activeFundsEntries = (c: TRoadblockCaseData, types: ReadonlyArray<TCaseLedgerType>) =>
    c.case_ledger_entries.filter((l) => types.includes(l.type) && l.deleted_at === null);

const fundsRequested = (c: TRoadblockCaseData, types: ReadonlyArray<TCaseLedgerType>) =>
    activeFundsEntries(c, types).length > 0;

const fundsReceived = (c: TRoadblockCaseData, types: ReadonlyArray<TCaseLedgerType>) => {
    const entries = activeFundsEntries(c, types);
    return entries.length > 0 && entries.every((l) => l.paid_at !== null);
};

const clientFundsTypeDisplay = (type: TCaseLedgerType): string =>
    type === "money_from_client_initial_payment" ? "Initial payment"
    : type === "money_from_client_deposit" ? "Deposit"
    : type === "money_from_client_shortfall" ? "Shortfall"
    : "Money from client";

const hasAnyCharge = (c: TRoadblockCaseData) => c.cases_charges.length > 0;

const everyChargeHasRedemptionStatement = (c: TRoadblockCaseData) =>
    c.cases_charges.length > 0
    && c.cases_charges.every((ch) => ch.has_redemption_statement);

export const CompletionReady: TRoadblockConfiguration = {
    type: "completion_ready",
    display_name: "Completion Ready",
    only_create_if_resolved: [],
    requires_authoriser_signoff: false,
    blocks_exchange: false,
    create: {
        create_condition_description: "Cases at ready_for_exchange or exchanged status",
        predicate: (c) => c.case.status === "ready_for_exchange" || c.case.status === "exchanged",
    },
    checklist: [
        {
            type: "funds_requested_from_lender",
            is_resolved_by_short_description: "Funds requested from lender",
            create: {
                create_condition_description: "Cases with an active 'Mortgage advance' ledger entry",
                predicate: (c) => fundsRequested(c, LENDER_FUNDS_TYPES),
            },
            resolve: {
                is_resolved_by_long_description: `Adding a 'Mortgage advance' ledger entry to the case.`,
                predicate: (c) => fundsRequested(c, LENDER_FUNDS_TYPES),
                evidence: (c) => {
                    const requested = fundsRequested(c, LENDER_FUNDS_TYPES);
                    return [
                        fact(
                            "Mortgage advance",
                            requested ? "Requested" : "Not requested",
                            requested,
                            clientFundsLink,
                        ),
                    ];
                },
            },
        },
        {
            type: "funds_received_from_lender",
            is_resolved_by_short_description: "Funds received from lender",
            create: {
                create_condition_description: "Cases with an active 'Mortgage advance' ledger entry",
                predicate: (c) => fundsRequested(c, LENDER_FUNDS_TYPES),
            },
            resolve: {
                is_resolved_by_long_description: `Marking the 'Mortgage advance' ledger entry as paid.`,
                predicate: (c) => fundsReceived(c, LENDER_FUNDS_TYPES),
                evidence: (c) => {
                    const entry = activeFundsEntries(c, LENDER_FUNDS_TYPES).find((l) => l.paid_at !== null)
                        ?? activeFundsEntries(c, LENDER_FUNDS_TYPES)[0];
                    return [
                        fact(
                            "Mortgage advance",
                            entry?.paid_at ? `Received ${formatEvidenceDate(entry.paid_at)}` : "Not received",
                            fundsReceived(c, LENDER_FUNDS_TYPES),
                            clientFundsLink,
                        ),
                    ];
                },
            },
        },

        {
            type: "funds_requested_from_client",
            is_resolved_by_short_description: "Funds requested from client",
            create: {
                create_condition_description: "Cases with at least one active 'Money from client' ledger entry",
                predicate: (c) => fundsRequested(c, CLIENT_FUNDS_TYPES),
            },
            resolve: {
                is_resolved_by_long_description: `Adding a 'Money from client' ledger entry to the case.`,
                predicate: (c) => fundsRequested(c, CLIENT_FUNDS_TYPES),
                evidence: (c) => {
                    const requested = fundsRequested(c, CLIENT_FUNDS_TYPES);
                    return [
                        fact(
                            "Money from client",
                            requested ? "Requested" : "Not requested",
                            requested,
                            clientFundsLink,
                        ),
                    ];
                },
            },
        },
        {
            type: "funds_received_from_client",
            is_resolved_by_short_description: "Funds received from client",
            create: {
                create_condition_description: "Cases with at least one active 'Money from client' ledger entry",
                predicate: (c) => fundsRequested(c, CLIENT_FUNDS_TYPES),
            },
            resolve: {
                is_resolved_by_long_description: `Marking every active 'Money from client' ledger entry on the case as paid.`,
                predicate: (c) => fundsReceived(c, CLIENT_FUNDS_TYPES),
                evidence: (c) => activeFundsEntries(c, CLIENT_FUNDS_TYPES).map((entry) =>
                    fact(
                        clientFundsTypeDisplay(entry.type),
                        entry.paid_at ? `Received ${formatEvidenceDate(entry.paid_at)}` : "Not received",
                        entry.paid_at !== null,
                        clientFundsLink,
                    ),
                ),
            },
        },

        {
            type: "redemption_statement_for_completion_received",
            is_resolved_by_short_description: "Redemption statement(s) received for completion",
            create: {
                create_condition_description: "Cases with at least one charge to redeem",
                predicate: (c) => hasAnyCharge(c),
            },
            resolve: {
                is_resolved_by_long_description: `Every charge on the case having a redemption statement file attached. Validity-window check (statement not expired against completion date) is a follow-up.`,
                predicate: (c) => everyChargeHasRedemptionStatement(c),
                evidence: (c) => c.cases_charges.map((charge) =>
                    fact(
                        charge.account_number.trim().length > 0 ? `Charge ${charge.account_number}` : "Charge",
                        charge.has_redemption_statement ? "Statement attached" : "No statement",
                        charge.has_redemption_statement,
                        chargesLink,
                    ),
                ),
            },
        },

        {
            type: "seller_solicitor_paid",
            is_resolved_by_short_description: "Seller solicitor paid",
            create: {
                create_condition_description: "Purchase cases",
                predicate: (c) => c.case.transaction_type === "purchase",
            },
            resolve: {
                is_resolved_by_long_description: `Recording the date the case paid out completion monies to the seller's solicitor.`,
                predicate: (c) => c.case.seller_solicitor_paid !== null,
                evidence: (c) => [
                    fact(
                        "Seller solicitor paid",
                        c.case.seller_solicitor_paid !== null ? `Paid ${formatEvidenceDate(c.case.seller_solicitor_paid)}` : "Not paid",
                        c.case.seller_solicitor_paid !== null,
                        outgoingPaymentsLink,
                    ),
                ],
            },
        },
        {
            type: "seller_conveyancer_bank_account_verified",
            is_resolved_by_short_description: "Seller conveyancer bank account verified",
            create: {
                create_condition_description: "Purchase cases with a received signed TA13",
                predicate: (c) => c.case.transaction_type === "purchase" && anyReceivedDocumentExists(c, ["signed_ta13"]),
            },
            resolve: {
                is_resolved_by_long_description: `Running the Lawyer Checker verification on the seller conveyancer's bank account and recording the result — a check URL must be stored and the status must no longer be "unknown".`,
                predicate: (c) =>
                    c.case.seller_conveyancer_bank_account_lawyer_checker_url.trim() !== ""
                    && c.case.seller_conveyancer_bank_account_verification_status !== "unknown",
                evidence: (c) => {
                    const hasUrl = c.case.seller_conveyancer_bank_account_lawyer_checker_url.trim() !== "";
                    const status = c.case.seller_conveyancer_bank_account_verification_status;
                    return [
                        fact(
                            "Lawyer Checker result",
                            !hasUrl ? "Not run"
                            : status === "unknown" ? "Run, no result recorded"
                            : status,
                            hasUrl && status !== "unknown",
                            outgoingPaymentsLink,
                        ),
                    ];
                },
            },
        },
        {
            type: "seller_solicitor_payment_confirmed",
            is_resolved_by_short_description: "Seller solicitor payment confirmed",
            create: {
                create_condition_description: "Purchase cases",
                predicate: (c) => c.case.transaction_type === "purchase",
            },
            resolve: {
                is_resolved_by_long_description: `Recording the date the seller's solicitor confirmed receipt of completion monies.`,
                predicate: (c) => c.case.seller_solicitor_received_payment !== null,
                evidence: (c) => [
                    fact(
                        "Receipt confirmed",
                        c.case.seller_solicitor_received_payment !== null ? `Confirmed ${formatEvidenceDate(c.case.seller_solicitor_received_payment)}` : "Not confirmed",
                        c.case.seller_solicitor_received_payment !== null,
                        outgoingPaymentsLink,
                    ),
                ],
            },
        },

        {
            type: "proceeds_of_sale_received_from_buyer_solicitor",
            is_resolved_by_short_description: "Proceeds of sale received from buyer's solicitor",
            create: {
                create_condition_description: "Sale cases",
                predicate: (c) => c.case.transaction_type === "sale",
            },
            resolve: {
                is_resolved_by_long_description: `Marking the proceeds of sale as received from the buyer's solicitor.`,
                predicate: (c) => c.case.proceeds_of_sale_received_from_buyer_solicitor !== null,
                evidence: (c) => [
                    fact(
                        "Proceeds received",
                        c.case.proceeds_of_sale_received_from_buyer_solicitor !== null ? `Received ${formatEvidenceDate(c.case.proceeds_of_sale_received_from_buyer_solicitor)}` : "Not received",
                        c.case.proceeds_of_sale_received_from_buyer_solicitor !== null,
                        clientFundsLink,
                    ),
                ],
            },
        },

        {
            type: "divorce_document_received",
            is_resolved_by_short_description: "Divorce document received",
            create: {
                create_condition_description: "Divorce cases on transfer/remortgage/purchase that have picked a document type",
                predicate: (c) =>
                    c.case.seller_reason_for_sale === "divorce"
                    && (
                        c.case.transaction_type === "transfer"
                        || c.case.transaction_type === "remortgage"
                        || c.case.transaction_type === "purchase"
                    )
                    && c.case.divorce_document_type !== "unknown",
            },
            resolve: {
                is_resolved_by_long_description: `A scanned copy of the chosen divorce document (the type selected on contract details) being uploaded to the doc store and marked as received. A generic 'court_order' tile with a received date also satisfies this — historically operators uploaded divorce orders there before the typed tiles existed.`,
                predicate: (c) =>
                    c.cases_documents.some(
                        (d) =>
                            (d.type === c.case.divorce_document_type || d.type === "court_order")
                            && d.received_date !== null,
                    ),
                evidence: (c) => {
                    const received = c.cases_documents.find(
                        (d) =>
                            (d.type === c.case.divorce_document_type || d.type === "court_order")
                            && d.received_date !== null,
                    );
                    return [
                        neutralFact("Document type", c.case.divorce_document_type),
                        fact(
                            "Divorce document",
                            received?.received_date ? `Received ${formatEvidenceDate(received.received_date)}` : "Not received",
                            received != null,
                        ),
                    ];
                },
            },
        },
    ],
};
